import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Pencil, FileDown, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const dl = (dataUrl, filename) => {
  const bin = atob(dataUrl.split(',')[1]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
const FIELDS = [
  ['basic_salary', 'Basic Salary'],
  ['house_rent_allowance', 'House Rent Allowance (HRA)'],
  ['utilities_allowance', 'Utilities Allowance'],
  ['mobile_allowance', 'Mobile Allowance'],
  ['conveyance_allowance', 'Conveyance Allowance'],
  ['special_allowance', 'Special Allowance'],
  ['tax_deduction', 'Tax Deduction'],
  ['other_deductions', 'Other Deductions'],
];
const emptyStruct = Object.fromEntries(FIELDS.map(([k]) => [k, 0]));

export default function PayrollManagement() {
  const [users, setUsers] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [structForm, setStructForm] = useState({ user_id: '', ...emptyStruct });
  const [payslip, setPayslip] = useState({ open: false, user_id: null, month: new Date().toISOString().slice(0, 7) });
  const [advance, setAdvance] = useState({ open: false, user_id: null, amount: '', reason: '', to_be_recovered_in_months: 1 });

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [u, s] = await Promise.all([
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/payroll/structures`, { withCredentials: true }),
      ]);
      setUsers(u.data); setStructures(s.data);
    } catch { toast.error('Failed to load payroll data'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openEdit = async (userId) => {
    const { data } = await axios.get(`${API_URL}/api/payroll/structure/${userId}`, { withCredentials: true });
    setStructForm({ user_id: userId, ...emptyStruct, ...(data || {}) });
    setDialog(true);
  };

  const saveStructure = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...structForm };
      FIELDS.forEach(([k]) => { payload[k] = Number(payload[k] || 0); });
      await axios.post(`${API_URL}/api/payroll/structure`, payload, { withCredentials: true });
      toast.success('Salary structure saved');
      setDialog(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const issuePayslip = async () => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/payroll/payslip/${payslip.user_id}?month=${encodeURIComponent(payslip.month)}`,
        {}, { withCredentials: true });
      dl(data.pdf, `Payslip_${data.slip_number}.pdf`);
      toast.success(`Payslip ${data.slip_number} generated`);
      setPayslip({ open: false, user_id: null, month: new Date().toISOString().slice(0, 7) });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to issue payslip'); }
  };

  const recordAdvance = async () => {
    try {
      await axios.post(`${API_URL}/api/payroll/advance`, {
        user_id: advance.user_id, amount: Number(advance.amount),
        reason: advance.reason, to_be_recovered_in_months: Number(advance.to_be_recovered_in_months || 1)
      }, { withCredentials: true });
      toast.success('Advance recorded');
      setAdvance({ open: false, user_id: null, amount: '', reason: '', to_be_recovered_in_months: 1 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const findStructure = (uid) => structures.find(s => s.user_id === uid) || null;

  return (
    <div className="space-y-6" data-testid="payroll-management">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2" data-testid="payroll-title">Payroll</h1>
        <p className="text-slate-400">Configure salaries, issue payslips and record advances</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          {loading ? <div className="text-center py-8 text-slate-400">Loading...</div>
            : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Employee</TableHead>
                    <TableHead className="text-slate-400">Role</TableHead>
                    <TableHead className="text-slate-400">Gross</TableHead>
                    <TableHead className="text-slate-400">Deductions</TableHead>
                    <TableHead className="text-slate-400">Net</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const s = findStructure(u.id);
                    return (
                      <TableRow key={u.id} className="border-slate-800" data-testid="payroll-row">
                        <TableCell className="text-white font-mono" data-testid={`payroll-emp-${u.id}`}>{u.employee_id || u.username}</TableCell>
                        <TableCell className="text-emerald-500">{u.role}</TableCell>
                        <TableCell className="text-slate-300">PKR {s?.gross_salary?.toLocaleString() || '0'}</TableCell>
                        <TableCell className="text-slate-300">PKR {s?.total_deductions?.toLocaleString() || '0'}</TableCell>
                        <TableCell className="text-emerald-500">PKR {s?.net_salary?.toLocaleString() || '0'}</TableCell>
                        <TableCell>
                          <div className="flex space-x-1">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(u.id)}
                              data-testid={`edit-structure-${u.id}`}
                              className="text-blue-400 hover:bg-slate-800 h-8 w-8 p-0" title="Edit structure">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {s && (
                              <>
                                <Button size="sm" variant="ghost"
                                  onClick={() => setPayslip({ open: true, user_id: u.id, month: new Date().toISOString().slice(0, 7) })}
                                  data-testid={`payslip-${u.id}`}
                                  className="text-emerald-500 hover:bg-slate-800 h-8 w-8 p-0" title="Payslip">
                                  <FileDown className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost"
                                  onClick={() => setAdvance({ open: true, user_id: u.id, amount: '', reason: '', to_be_recovered_in_months: 1 })}
                                  data-testid={`advance-${u.id}`}
                                  className="text-amber-500 hover:bg-slate-800 h-8 w-8 p-0" title="Advance">
                                  <Wallet className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle>Salary Structure</DialogTitle>
            <DialogDescription className="text-slate-400">Configure basic pay, allowances and deductions.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveStructure} className="grid grid-cols-2 gap-4">
            {FIELDS.map(([k, l]) => (
              <div key={k} className="space-y-2">
                <Label className="text-slate-300">{l}</Label>
                <Input type="number" value={structForm[k] ?? 0}
                  data-testid={`struct-${k}`}
                  onChange={(e) => setStructForm({ ...structForm, [k]: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
            ))}
            <div className="col-span-2 flex justify-end space-x-2">
              <Button type="button" variant="ghost" onClick={() => setDialog(false)} className="text-slate-300">Cancel</Button>
              <Button type="submit" data-testid="save-structure" className="bg-emerald-600 hover:bg-emerald-700">Save Structure</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={payslip.open} onOpenChange={(o) => setPayslip({ ...payslip, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader><DialogTitle>Issue Payslip</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-slate-300">Month (YYYY-MM)</Label>
            <Input type="month" value={payslip.month}
              data-testid="payslip-month"
              onChange={(e) => setPayslip({ ...payslip, month: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white" />
            <div className="flex justify-end">
              <Button onClick={issuePayslip} data-testid="submit-payslip" className="bg-emerald-600 hover:bg-emerald-700">
                <FileDown className="h-4 w-4 mr-2" /> Generate & Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={advance.open} onOpenChange={(o) => setAdvance({ ...advance, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader><DialogTitle>Record Advance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="number" value={advance.amount} placeholder="Amount (PKR)"
              data-testid="advance-amount"
              onChange={(e) => setAdvance({ ...advance, amount: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white" />
            <Input value={advance.reason} placeholder="Reason"
              onChange={(e) => setAdvance({ ...advance, reason: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white" />
            <Input type="number" value={advance.to_be_recovered_in_months} placeholder="Recovery months"
              onChange={(e) => setAdvance({ ...advance, to_be_recovered_in_months: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white" />
            <div className="flex justify-end">
              <Button onClick={recordAdvance} data-testid="submit-advance" className="bg-amber-500 hover:bg-amber-600">Record Advance</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
